import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { api } from '@/api';
import { toast } from 'sonner';
import { Check, X, Clock, AlertTriangle, User, Calendar, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

export default function EventApprovalWidget({ projectId, onApprovalChange }) {
  const { user } = useAuth();
  const [pendingEvents, setPendingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingEvent, setRejectingEvent] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);

  // Only show for roles that can approve
  const canApprove = user?.role && ['admin', 'director', 'project_director', 'project_manager',
    'contracts_manager', 'commercial_manager', 'construction_manager',
    'site_agent', 'site_manager', 'site_engineer',
    'contracts_director'].includes(user.role);

  useEffect(() => {
    if (projectId && canApprove) {
      loadPendingEvents();
    }
  }, [projectId, canApprove]);

  const loadPendingEvents = async () => {
    try {
      setLoading(true);
      const response = await api.events.getPendingApproval(projectId);
      setPendingEvents(response.data || []);
    } catch (error) {
      console.error('Failed to load pending events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (eventId) => {
    try {
      setProcessing(true);
      await api.events.approve(eventId);
      toast.success('Event approved successfully');
      loadPendingEvents();
      onApprovalChange?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve event');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectingEvent) return;
    try {
      setProcessing(true);
      await api.events.reject(rejectingEvent.id, rejectReason);
      toast.success('Event rejected');
      setRejectDialogOpen(false);
      setRejectingEvent(null);
      setRejectReason('');
      loadPendingEvents();
      onApprovalChange?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject event');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-ZA', { 
      day: '2-digit', month: 'short', year: 'numeric' 
    });
  };

  if (!canApprove || pendingEvents.length === 0) {
    return null;
  }

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <AlertTriangle className="h-5 w-5 text-amber-600" />
        <h3 className="font-bold text-amber-800 uppercase text-sm tracking-wide">
          Events Pending Approval ({pendingEvents.length})
        </h3>
      </div>

      {loading ? (
        <div className="text-center py-4 text-amber-600">Loading...</div>
      ) : (
        <div className="space-y-3">
          {pendingEvents.map((event) => (
            <div 
              key={event.id} 
              className="bg-white border border-amber-200 rounded-sm overflow-hidden"
              data-testid={`pending-event-${event.id}`}
            >
              {/* Header */}
              <div 
                className="p-3 flex items-center justify-between cursor-pointer hover:bg-amber-50"
                onClick={() => setExpandedId(expandedId === event.id ? null : event.id)}
              >
                <div className="flex items-center gap-3">
                  <Clock className="h-4 w-4 text-amber-500" />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{event.event_type}</span>
                      <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700 border-amber-300">
                        Pending
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-500">
                      By {event.creator_name || 'Unknown'} ({event.creator_role || 'Unknown'}) • {formatDate(event.created_at)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {event.can_approve && (
                    <>
                      <Button
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleApprove(event.id); }}
                        disabled={processing}
                        className="bg-green-600 hover:bg-green-700 text-white h-8"
                        data-testid={`approve-event-${event.id}`}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          setRejectingEvent(event);
                          setRejectDialogOpen(true);
                        }}
                        disabled={processing}
                        className="text-red-600 hover:text-red-700 h-8"
                        data-testid={`reject-event-${event.id}`}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Reject
                      </Button>
                    </>
                  )}
                  {expandedId === event.id ? (
                    <ChevronUp className="h-4 w-4 text-slate-400" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  )}
                </div>
              </div>

              {/* Expanded Details */}
              {expandedId === event.id && (
                <div className="border-t border-amber-200 p-4 bg-slate-50">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-xs font-bold uppercase text-slate-500">Responsible Party</p>
                      <p className="text-sm">{event.responsible_party || 'Not specified'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase text-slate-500">Start Date</p>
                      <p className="text-sm font-mono">{formatDate(event.start_date)}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase text-slate-500 mb-1">Narrative</p>
                    <p className="text-sm text-slate-700 bg-white p-3 border rounded-sm whitespace-pre-wrap">
                      {event.narrative || 'No narrative provided'}
                    </p>
                  </div>
                  {event.attachments?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-bold uppercase text-slate-500 mb-1">
                        Attachments ({event.attachments.length})
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {event.attachments.map((att, idx) => (
                          <a 
                            key={idx}
                            href={att.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline bg-blue-50 px-2 py-1 rounded"
                          >
                            {att.file_name}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-barlow font-bold uppercase text-red-700">
              Reject Event
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              You are rejecting the event: <strong>{rejectingEvent?.event_type}</strong>
            </p>
            <div>
              <Label className="text-xs font-bold uppercase text-slate-500">Rejection Reason</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Provide a reason for rejection (optional but recommended)"
                rows={3}
                className="rounded-sm resize-none"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleReject} 
                disabled={processing}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <X className="h-4 w-4 mr-2" />
                Confirm Rejection
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
